// ResumeProjectsSection.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Snackbar,
  Alert,
  Tooltip
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";

import { SortableSection } from "./SortableSection";
import { SortableCredit } from "./SortableCredit";
import EmptySkleton from "./EmptySkleton";
import api from "../../api/api";

/* ----------------------------------------
   Helper: Normalize URLs
---------------------------------------- */
const normalizeUrl = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
};

const ResumeProjectsSection = ({
  groupedResumeProjects = [],
  editMode,
  setResumeDialogOpen,
  setSelectedSectionTitle,
  setSelectedSectionId,
  setEditInitialData,
  setEditSection,
  setEditPromptOpen,
  setSectionToDelete,
  setDeleteSectionDialogOpen,
  setProjectToDelete,
  setDeleteProjectDialogOpen
}) => {
  const [sections, setSections] = useState([]);
  const [reorderMode, setReorderMode] = useState(false);
  const [hasReordered, setHasReordered] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  /* ----------------------------------------------------
     Build sections + credits from API response
  ---------------------------------------------------- */
  useEffect(() => {
    if (!Array.isArray(groupedResumeProjects)) {
      setSections([]);
      return;
    }

    const sectionMap = new Map();

    groupedResumeProjects.forEach(item => {
      const sectionId = item.resumeSectionId;
      const heading =
        item.sectionHeading ||
        item.resumeSectionName ||
        "Untitled Section";

      if (!sectionMap.has(sectionId)) {
        sectionMap.set(sectionId, {
          resumeSectionId: sectionId,
          sectionHeading: heading,
          sortOrder: item.sectionSortOrder || 0,
          credits: []
        });
      }

      if (item.resumeSectionDetailId != null) {
        sectionMap.get(sectionId).credits.push({
          resumeSectionDetailId: item.resumeSectionDetailId,
          resumeSectionId: sectionId,
          title: item.title,
          role: item.role,
          company: item.company,
          url: item.url,
          sortOrder: item.detailSortOrder || 0
        });
      }
    });

    const sortedSections = Array.from(sectionMap.values()).sort(
      (a, b) => a.sortOrder - b.sortOrder
    );

    sortedSections.forEach(section => {
      section.credits.sort((a, b) => a.sortOrder - b.sortOrder);
    });

    setSections(sortedSections);
  }, [groupedResumeProjects]);

  const hasAnyCredits = sections.some(s => s.credits.length > 0);

  /* ----------------------------------------------------
     DnD sensors
  ---------------------------------------------------- */
const sensors = useSensors(
  useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8,   // prevents accidental drag while scrolling
    },
  }),
  useSensor(TouchSensor, {
    activationConstraint: {
      delay: 150,   // long press to drag
      tolerance: 5,
    },
  }),
  useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates,
  })
);

  /* ----------------------------------------------------
     Section reorder
  ---------------------------------------------------- */
  const onSectionDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = sections.findIndex(
      s => s.resumeSectionId === active.id
    );
    const newIndex = sections.findIndex(
      s => s.resumeSectionId === over.id
    );

    const reordered = arrayMove(sections, oldIndex, newIndex).map(
      (s, index) => ({
        ...s,
        sortOrder: index + 1
      })
    );

    setSections(reordered);
    setHasReordered(true);
  };

  /* ----------------------------------------------------
     Credit reorder
  ---------------------------------------------------- */
  const onCreditDragEnd = sectionId => ({ active, over }) => {
    if (!over || active.id === over.id) return;

    setSections(prev =>
      prev.map(section => {
        if (section.resumeSectionId !== sectionId) return section;

        const oldIndex = section.credits.findIndex(
          c => c.resumeSectionDetailId === active.id
        );
        const newIndex = section.credits.findIndex(
          c => c.resumeSectionDetailId === over.id
        );

        const reorderedCredits = arrayMove(
          section.credits,
          oldIndex,
          newIndex
        ).map((c, index) => ({
          ...c,
          sortOrder: index + 1
        }));

        return { ...section, credits: reorderedCredits };
      })
    );

    setHasReordered(true);
  };

  /* ----------------------------------------------------
     Persist order to backend
  ---------------------------------------------------- */
  const saveReorderToBackend = async () => {
    const userId = Number(localStorage.getItem("userId"));

    try {
      for (const section of sections) {
        await api.post("/api/TalentProfile/AddOrUpdateResumeSection", {
          resumeSectionId: section.resumeSectionId,
          userId,
          userBy: userId,
          sectionHeading: section.sectionHeading,
          sectionSortOrder: section.sortOrder,
          activeFlag: true
        });
      }

      for (const section of sections) {
        for (const credit of section.credits) {
          await api.post(
            "/api/TalentProfile/AddOrUpdateResumeSectionDetails",
            {
              resumeSectionDetailId: credit.resumeSectionDetailId,
              resumeSectionId: section.resumeSectionId,
              title: credit.title,
              role: credit.role,
              company: credit.company,
              url: credit.url,
              userId,
              sortOrder: credit.sortOrder,
              activeFlag: true
            }
          );
        }
      }

      setSaveSuccess(true);
    } catch (error) {
      console.error("Failed to save reorder", error);
    }
  };

  /* ----------------------------------------------------
     Action handlers
  ---------------------------------------------------- */
  const handleEditProject = item => {
    setEditInitialData(item);
    setEditSection("Credits");
    setEditPromptOpen(true);
  };

  const handleDeleteProject = item => {
    setProjectToDelete(item);
    setDeleteProjectDialogOpen(true);
  };

  const handleAddRole = (heading, sectionId) => {
    setSelectedSectionTitle(heading);
    setSelectedSectionId(sectionId);
    setResumeDialogOpen(true);
    setEditInitialData({ sectionHeading: heading });
  };

  const handleDeleteSection = (heading, sectionId) => {
    setSectionToDelete({ heading, resumeSectionId: sectionId });
    setDeleteSectionDialogOpen(true);
  };

  const showActions = editMode && !reorderMode;

  if (!hasAnyCredits && !editMode) return null;

  /* ----------------------------------------------------
     Render
  ---------------------------------------------------- */
  return (
    <Box sx={{ backgroundColor: "#fff", p: 1, mt: 0.5 }} className="shadow margin0">

      {/* Header */}
      {hasAnyCredits && (
        <Box
          display="flex"
          sx={{
            fontWeight: "bold",
            color: "#202020",
            pb: 1,
            ml: 1,
            fontSize: "1rem",
            borderBottom: "1px solid #dedede"
          }}
        >
          <Box flex={1}>Project</Box>
          <Box flex={1}>Role</Box>
          <Box flex={1}>Director / Prod. Co.</Box>
        </Box>
      )}

      {/* EMPTY STATE: No categories */}
      {editMode && sections.length === 0 && (
        <EmptySkleton
          title="Credits"
          subtitle="No categories yet. Add now."
          editMode
          showDivider
          onAdd={() => setEditSection("AddProjectSection")}
        />
      )}

      {/* NORMAL VIEW (hidden in reorder mode) */}
      {!reorderMode && sections.map(section => (
        <Box key={section.resumeSectionId} mb={3} ml={1}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={1}
          >
            <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
              {section.sectionHeading}
            </Typography>

            {showActions && (
              <IconButton
                onClick={() =>
                  handleDeleteSection(
                    section.sectionHeading,
                    section.resumeSectionId
                  )
                }
              >
                <DeleteIcon fontSize="small" color="error" />
              </IconButton>
            )}
          </Box>

          {/* Roles */}
          <Box>
            {section.credits.map(item => (
              <Box
                key={item.resumeSectionDetailId}
                display="flex"
                alignItems="center"
                sx={{ my: 1, gap: 1 , fontSize:"0.95rem"}}
              >
                <Box flex={1}>
                  {item.url ? (
                    <Tooltip title="Open project link" arrow>
                      <a
                        href={normalizeUrl(item.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#1976d2",
                          fontWeight: 500
                        }}
                      >
                        {item.title}
                      </a>
                    </Tooltip>
                  ) : (
                    item.title
                  )}
                </Box>

                <Box flex={1}>{item.role}</Box>
                <Box flex={1}>{item.company}</Box>

                {showActions && (
                  <Box>
                    <IconButton onClick={() => handleEditProject(item)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton onClick={() => handleDeleteProject(item)}>
                      <DeleteIcon fontSize="small" color="error" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            ))}
          </Box>

          {/* Add Role Skeleton */}
          {editMode && (
            <Box px={2} sx={{fontSize:"0.95rem"}}>
              <EmptySkleton
                title={`Add another ${section.sectionHeading} credit`}
                subtitle=""
                editMode
                showDivider
                onAdd={() =>
                  handleAddRole(
                    section.sectionHeading,
                    section.resumeSectionId
                  )
                }
              />
            </Box>
          )}
        </Box>
      ))}

      {/* Add Category Skeleton */}
      {editMode && !reorderMode && sections.length > 0 && (
        <EmptySkleton
          title="ADD NEW CREDITS"
          subtitle="You can add more credits like Film, Theatre, TV."
          editMode
          showDivider={false}
          onAdd={() => setEditSection("AddProjectSection")}
        />
      )}

      {/* REORDER MODE */}
      {hasAnyCredits && reorderMode && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onSectionDragEnd}
        >
          <SortableContext
            items={sections.map(s => s.resumeSectionId)}
            strategy={verticalListSortingStrategy}
          >
            {sections.map(section => (
              <SortableSection
                key={section.resumeSectionId}
                id={section.resumeSectionId}
              >
                <Box mb={3} sx={{ backgroundColor: "#fafbfc", borderRadius: 1 }}>
                  <Typography sx={{ fontWeight: "bold", ml: 1 }}>
                    {section.sectionHeading}
                  </Typography>

                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={onCreditDragEnd(section.resumeSectionId)}
                  >
                    <SortableContext
                      items={section.credits.map(c => c.resumeSectionDetailId)}
                      strategy={verticalListSortingStrategy}
                    >
                      {section.credits.map(item => (
                        <SortableCredit
                          key={item.resumeSectionDetailId}
                          id={item.resumeSectionDetailId}
                        >
                          <Box display="flex" sx={{ py: 1, ml: 1 }}>
                            <Box flex={1}>{item.title}</Box>
                            <Box flex={1}>{item.role}</Box>
                            <Box flex={1}>{item.company}</Box>
                          </Box>
                        </SortableCredit>
                      ))}
                    </SortableContext>
                  </DndContext>
                </Box>
              </SortableSection>
            ))}
          </SortableContext>
        </DndContext>
      )}

      {/* Reorder Button */}
      {editMode && (
        <Box display="flex" gap={1} mt={2} ml={1}>
          <Button
            size="small"
            color="secondary"
            variant={reorderMode ? "contained" : "outlined"}
            onClick={async () => {
              if (reorderMode && hasReordered) {
                await saveReorderToBackend();
              }
              setReorderMode(!reorderMode);
              setHasReordered(false);
            }}
          >
            {reorderMode ? "Done Reordering" : "Reorder"}
          </Button>
        </Box>
      )}

      {/* Save Success Toast */}
      <Snackbar
        open={saveSuccess}
        autoHideDuration={3000}
        onClose={() => setSaveSuccess(false)}
      >
        <Alert severity="success" variant="filled">
          Order saved successfully
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ResumeProjectsSection;
